import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import { Spin, Alert } from 'antd';
import { fetchRealtimeData } from '../services/chartDataService';
import { ChartDataTransformer } from '../utils/chartDataTransformer';
import { ChartOptionsFactory } from '../utils/chartOptionsFactory';
import type { RealtimeChartProps } from '../types/chart.types';
import type { ChartDataResponse } from '../types/chart.types';

const DEFAULT_UPDATE_INTERVAL_MS = 60000; // 60秒
const CHART_HEIGHT = 600;
const CHART_HEIGHT_MOBILE = 400;

/**
 * リアルタイムチャートコンポーネント
 */
export const RealtimeChart: React.FC<RealtimeChartProps> = ({
  panelId,
  selectedDataTypes,
  updateIntervalMs = DEFAULT_UPDATE_INTERVAL_MS,
}) => {
  const [chartData, setChartData] = useState<ChartDataResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  // EChartsインスタンスへの参照
  const chartInstanceRef = useRef<ReturnType<ReactECharts['getEchartsInstance']> | null>(null);
  // 初回レンダリングフラグ
  const isInitialRenderRef = useRef<boolean>(true);
  // dataZoomの状態を保持
  const dataZoomStateRef = useRef<{ start: number; end: number } | null>(null);

  // モバイル判定
  useEffect(() => {
    const checkMobile = (): void => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // データ取得関数
  const fetchData = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      const data = await fetchRealtimeData({ panelId, dataTypes: selectedDataTypes });
      setChartData(data);
      setLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'データ取得に失敗しました';
      setError(errorMessage);
      setLoading(false);
    }
  }, [panelId, selectedDataTypes]);

  // 初回データ取得
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // 定期更新
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, updateIntervalMs);

    return () => clearInterval(interval);
  }, [fetchData, updateIntervalMs]);

  // selectedDataTypesが変更された時にチャートをリセットするためのキー
  const chartKey = useMemo(() => {
    return JSON.stringify(selectedDataTypes);
  }, [selectedDataTypes]);

  // EChartsシリーズデータに変換（useMemoでメモ化）
  const { seriesData, originalPoints } = useMemo(() => {
    if (!chartData) {
      return { seriesData: [], originalPoints: [] };
    }
    const result = ChartDataTransformer.transformToEChartsSeries({
      dataPoints: chartData.dataPoints,
      selectedDataTypes,
    });
    return {
      seriesData: result.series,
      originalPoints: result.originalPoints
    };
  }, [chartData, selectedDataTypes]);

  // チャートオプション生成（useMemoでメモ化）
  const chartOptions = useMemo(() => {
    return ChartOptionsFactory.createRealtimeChartOptions({ seriesData, originalDataPoints: originalPoints, isMobile });
  }, [seriesData, originalPoints, isMobile]);

  // チャートインスタンス取得時のコールバック
  const handleChartReady = useCallback((chartInstance: ReturnType<ReactECharts['getEchartsInstance']>) => {
    chartInstanceRef.current = chartInstance;
    isInitialRenderRef.current = false;

    // dataZoomイベントをリッスン
    chartInstance.on('dataZoom', () => {
      const option = chartInstance.getOption();
      if (option && option.dataZoom && Array.isArray(option.dataZoom) && option.dataZoom[0]) {
        const zoom = option.dataZoom[0] as { start?: number; end?: number };
        if (zoom.start !== undefined && zoom.end !== undefined) {
          dataZoomStateRef.current = { start: zoom.start, end: zoom.end };
        }
      }
    });
  }, []);

  // データ更新時にseriesとxAxisを更新（ズーム状態を保持）
  useEffect(() => {
    if (!isInitialRenderRef.current && chartInstanceRef.current && seriesData.length > 0) {
      // 現在のズーム状態を取得
      const currentOption = chartInstanceRef.current.getOption();
      let currentZoom = dataZoomStateRef.current;

      // dataZoomStateRefが空の場合は現在のオプションから取得
      if (!currentZoom && currentOption && currentOption.dataZoom && Array.isArray(currentOption.dataZoom)) {
        const zoom = currentOption.dataZoom[0] as { start?: number; end?: number; type?: string; xAxisIndex?: number };
        if (zoom && zoom.start !== undefined && zoom.end !== undefined) {
          currentZoom = { start: zoom.start, end: zoom.end };
        }
      }

      // 新しいオプションを生成
      const newOptions = ChartOptionsFactory.createRealtimeChartOptions({ seriesData, originalDataPoints: originalPoints, isMobile });

      // seriesとmarkAreaを含む完全なオプションを更新（ズーム状態は保持）
      const updateOptions: Partial<typeof newOptions> = {
        series: newOptions.series,
      };

      // ズーム状態が変更されていた場合のみdataZoomを更新
      if (currentZoom) {
        updateOptions.dataZoom = [
          {
            type: 'inside',
            xAxisIndex: 0,
            start: currentZoom.start,
            end: currentZoom.end,
          },
        ];
      }

      // チャートを更新（notMerge=falseでマージ、lazyUpdate=falseで即座に更新）
      chartInstanceRef.current.setOption(updateOptions as never, false, false);
    }
  }, [seriesData]);

  // ローディング表示
  if (loading) {
    return (
      <div
        className="flex justify-center items-center"
        style={{
          height: isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT,
        }}
      >
        <Spin size="large" tip="データ読み込み中..." />
      </div>
    );
  }

  // エラー表示
  if (error) {
    return (
      <Alert
        message="エラー"
        description={error}
        type="error"
        showIcon
        className="mb-4"
      />
    );
  }

  // データなし
  if (!chartData || ChartDataTransformer.isEmpty(chartData.dataPoints)) {
    return (
      <Alert
        message="データなし"
        description="表示するデータがありません"
        type="info"
        showIcon
        className="mb-4"
      />
    );
  }

  return (
    <ReactECharts
      key={chartKey}
      option={chartOptions}
      style={{ height: isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT, width: '100%' }}
      onChartReady={handleChartReady}
      notMerge={false}
      lazyUpdate={true}
    />
  );
};
